'use client';

import { useState } from 'react';
import { CareerPath } from '@/lib/careerPaths';
import {
  getSalaryNegotiationData,
  formatSalary,
  getNegotiabilityColor,
} from '@/lib/salaryNegotiationData';
import styles from './SalaryNegotiation.module.css';

interface SalaryNegotiationProps {
  career: CareerPath;
}

type TabId = 'scripts' | 'tips' | 'benefits' | 'insights';

export default function SalaryNegotiation({ career }: SalaryNegotiationProps) {
  const [activeTab, setActiveTab] = useState<TabId>('scripts');
  const [expandedScript, setExpandedScript] = useState<number | null>(0);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const data = getSalaryNegotiationData(career);

  const handleCopy = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const tabs: { id: TabId; label: string; icon: string }[] = [
    { id: 'scripts', label: 'Negotiation Scripts', icon: '💬' },
    { id: 'tips', label: 'Pro Tips', icon: '💡' },
    { id: 'benefits', label: 'What to Negotiate', icon: '🎁' },
    { id: 'insights', label: 'Market Insights', icon: '📊' },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Salary Negotiation Toolkit</h3>
        <p className={styles.subtitle}>
          Strategies and scripts for {career.title} compensation
        </p>
      </div>

      {/* Market Rate Overview */}
      <div className={styles.marketRates}>
        <h4>Market Salary Ranges</h4>
        <div className={styles.rateCards}>
          <div className={styles.rateCard}>
            <span className={styles.rateLevel}>Entry Level</span>
            <span className={styles.rateRange}>
              {formatSalary(data.marketRates.entry.min)} - {formatSalary(data.marketRates.entry.max)}
            </span>
          </div>
          <div className={styles.rateCard}>
            <span className={styles.rateLevel}>Mid Level</span>
            <span className={styles.rateRange}>
              {formatSalary(data.marketRates.mid.min)} - {formatSalary(data.marketRates.mid.max)}
            </span>
          </div>
          <div className={styles.rateCard}>
            <span className={styles.rateLevel}>Senior Level</span>
            <span className={styles.rateRange}>
              {formatSalary(data.marketRates.senior.min)} - {formatSalary(data.marketRates.senior.max)}
            </span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={styles.tabNav}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.activeTab : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            <span className={styles.tabLabel}>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className={styles.content}>
        {/* Scripts Tab */}
        {activeTab === 'scripts' && (
          <div className={styles.scriptsTab}>
            <p className={styles.tabIntro}>
              Copy and customize these proven negotiation scripts for your situation.
            </p>
            <div className={styles.scriptsList}>
              {data.negotiationScripts.map((script, index) => (
                <div
                  key={index}
                  className={`${styles.scriptCard} ${expandedScript === index ? styles.expanded : ''}`}
                >
                  <button
                    className={styles.scriptHeader}
                    onClick={() => setExpandedScript(expandedScript === index ? null : index)}
                  >
                    <div className={styles.scriptMeta}>
                      <span className={styles.scenarioBadge}>{script.scenario}</span>
                      <p className={styles.contextText}>{script.context}</p>
                    </div>
                    <span className={styles.expandIcon}>
                      {expandedScript === index ? '−' : '+'}
                    </span>
                  </button>

                  {expandedScript === index && (
                    <div className={styles.scriptContent}>
                      <div className={styles.scriptBox}>
                        <div className={styles.scriptBoxHeader}>
                          <span>What to Say</span>
                          <button
                            className={styles.copyButton}
                            onClick={() => handleCopy(script.script, index)}
                          >
                            {copiedIndex === index ? '✓ Copied!' : 'Copy Script'}
                          </button>
                        </div>
                        <p className={styles.scriptText}>&ldquo;{script.script}&rdquo;</p>
                      </div>

                      <div className={styles.scriptTips}>
                        <h5>Tips for This Scenario</h5>
                        <ul>
                          {script.tips.map((tip, i) => (
                            <li key={i}>{tip}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tips Tab */}
        {activeTab === 'tips' && (
          <div className={styles.tipsTab}>
            <div className={styles.tipsGrid}>
              {data.tips.map((tip, index) => (
                <div key={index} className={styles.tipCard} data-category={tip.category}>
                  <div className={styles.tipHeader}>
                    <span className={styles.tipCategory}>{tip.category}</span>
                    <h4>{tip.title}</h4>
                  </div>
                  <p className={styles.tipContent}>{tip.content}</p>
                  {tip.example && (
                    <p className={styles.tipExample}>
                      <strong>Example:</strong> {tip.example}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className={styles.mistakesSection}>
              <h4>Common Mistakes to Avoid</h4>
              <ul className={styles.mistakesList}>
                {data.commonMistakes.map((mistake, i) => (
                  <li key={i}>
                    <span className={styles.xIcon}>✗</span>
                    {mistake}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Benefits Tab */}
        {activeTab === 'benefits' && (
          <div className={styles.benefitsTab}>
            <p className={styles.tabIntro}>
              Beyond base salary, these benefits are often negotiable and can significantly increase your total compensation.
            </p>
            <div className={styles.benefitsTable}>
              <div className={styles.benefitsHeader}>
                <span>Benefit</span>
                <span>Typical Value</span>
                <span>Negotiability</span>
              </div>
              {data.benefitsToNegotiate.map((benefit, index) => (
                <div key={index} className={styles.benefitRow}>
                  <span className={styles.benefitName}>{benefit.benefit}</span>
                  <span className={styles.benefitValue}>{benefit.typicalValue}</span>
                  <span
                    className={styles.negotiability}
                    style={{ color: getNegotiabilityColor(benefit.negotiability) }}
                  >
                    {benefit.negotiability.charAt(0).toUpperCase() + benefit.negotiability.slice(1)}
                  </span>
                </div>
              ))}
            </div>

            <div className={styles.leverageSection}>
              <h4>Your Leverage Points</h4>
              <p>Use these factors to strengthen your negotiation position:</p>
              <ul className={styles.leverageList}>
                {data.leveragePoints.map((point, i) => (
                  <li key={i}>
                    <span className={styles.checkIcon}>✓</span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Insights Tab */}
        {activeTab === 'insights' && (
          <div className={styles.insightsTab}>
            <div className={styles.insightsSection}>
              <h4>Industry Insights for {career.title}</h4>
              <ul className={styles.insightsList}>
                {data.industryInsights.map((insight, i) => (
                  <li key={i}>
                    <span className={styles.insightIcon}>💡</span>
                    {insight}
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.resourcesSection}>
              <h4>Salary Research Resources</h4>
              <div className={styles.resourceLinks}>
                <a
                  href="https://www.glassdoor.com/Salaries"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.resourceLink}
                >
                  <span>🔍</span>
                  Glassdoor Salaries
                </a>
                <a
                  href="https://www.levels.fyi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.resourceLink}
                >
                  <span>📊</span>
                  Levels.fyi
                </a>
                <a
                  href="https://www.payscale.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.resourceLink}
                >
                  <span>💰</span>
                  PayScale
                </a>
                <a
                  href="https://www.linkedin.com/salary"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.resourceLink}
                >
                  <span>💼</span>
                  LinkedIn Salary
                </a>
              </div>
            </div>

            <div className={styles.negotiationChecklist}>
              <h4>Pre-Negotiation Checklist</h4>
              <ul className={styles.checklistItems}>
                <li><input type="checkbox" /> Researched market rates for my role and location</li>
                <li><input type="checkbox" /> Identified my target salary and walkaway number</li>
                <li><input type="checkbox" /> Prepared 2-3 key accomplishments with metrics</li>
                <li><input type="checkbox" /> Listed my leverage points (skills, offers, timing)</li>
                <li><input type="checkbox" /> Practiced my negotiation script out loud</li>
                <li><input type="checkbox" /> Prepared questions about benefits and flexibility</li>
                <li><input type="checkbox" /> Ready to ask for time to consider the offer</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
